import React from 'react';
import { BarChart, Trash2, Layers, Calculator } from 'lucide-react';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент виджета сравнений налоговых режимов
 */
const ComparisonsWidget = ({ 
  savedCalculations, 
  deleteSavedCalculation, 
  loadSavedCalculation 
}) => {
  // Если нет сохраненных расчетов
  if (!savedCalculations || savedCalculations.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сравнение расчетов
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные варианты расчета налогов для сравнения между разными режимами и параметрами.
        </p>
      </div>
    );
  }

  // Получение названия налогового режима для отображения
  const getTaxSystemName = (taxSystem) => {
    switch (taxSystem) {
      case 'npd':
        return 'Самозанятость (НПД)';
      case 'usn_income':
        return 'УСН Доходы (6%)';
      case 'usn_profit':
        return 'УСН Доходы-Расходы (15%)';
      default:
        return 'Неизвестный режим';
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сравнение расчетов
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedCalculations.length} {savedCalculations.length === 1 ? 'вариант' : savedCalculations.length < 5 ? 'варианта' : 'вариантов'}
        </span>
      </div>

      <div className="space-y-4">
        {savedCalculations.map((calculation) => (
          <div 
            key={calculation.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {calculation.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Calculator className="w-4 h-4" />}
                  onClick={() => loadSavedCalculation(calculation.id)}
                  title="Загрузить параметры"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedCalculation(calculation.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span>Режим:</span>
                <span className="font-medium">
                  {getTaxSystemName(calculation.taxSystem)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Доход:</span>
                <span>
                  {formatCurrency(calculation.results.incomeAmount)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Налог:</span>
                <span>
                  {formatCurrency(calculation.results.calculatedTax)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Эффективная ставка:</span>
                <span className="font-semibold text-indigo-600 dark:text-indigo-400">
                  {formatNumber(calculation.results.effectiveTaxRate, 1)}%
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {savedCalculations.length > 1 && (
        <div className="mt-5 pt-4 border-t border-gray-200 dark:border-gray-700">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Сравнение налоговой нагрузки</h4>
          <div className="space-y-3">
            {savedCalculations.map(calculation => (
              <div key={`chart-${calculation.id}`} className="relative">
                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                  <span>{getTaxSystemName(calculation.taxSystem)}</span>
                  <span>{formatNumber(calculation.results.effectiveTaxRate, 1)}%</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 overflow-hidden">
                  <div 
                    className="h-2.5 rounded-full bg-indigo-600 dark:bg-indigo-500" 
                    style={{ width: `${Math.min(100, calculation.results.effectiveTaxRate * 2.5)}%` }}
                  ></div>
                </div>
                <div className="flex justify-between mt-1 text-xs text-gray-500 dark:text-gray-400">
                  <span>Налог: {formatCurrency(calculation.results.calculatedTax)}</span>
                  <span>Общая нагрузка: {formatCurrency(calculation.results.totalTaxBurden)}</span>
                </div>
              </div>
            ))}
          </div>
          
          <div className="flex justify-between items-center mt-4 pt-3 border-t border-gray-200 dark:border-gray-700 text-sm">
            <span className="text-gray-700 dark:text-gray-300">Оптимальный режим:</span>
            <span className="font-medium text-green-600 dark:text-green-400">
              {getTaxSystemName(
                savedCalculations
                  .slice()
                  .sort((a, b) => a.results.effectiveTaxRate - b.results.effectiveTaxRate)[0]
                  .taxSystem
              )}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComparisonsWidget;